import { useQuery } from '@tanstack/react-query';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { themes } from '@/themes/manifest';
import { useThemeStore } from '@/lib/theme';

// Azure Maps plumbing shared by every in-portal map (WWD fleet map, rig-modal mini-map).
// Auth material comes from the portal BFF (/api/maps/config): either the Maps shared key
// (Phase-0 bootstrap) or "anonymous" mode where the SDK pulls short-lived Entra tokens from
// /api/maps/token (minted by the BFF's managed identity). The browser then talks to
// atlas.microsoft.com directly — map tiles never transit the BFF.

export interface MapsConfigDto {
  mode: 'anonymous' | 'subscriptionKey' | 'unconfigured';
  clientId?: string;
  subscriptionKey?: string;
}

export function useMapsConfig() {
  return useQuery({
    queryKey: ['maps', 'config'],
    queryFn: async (): Promise<MapsConfigDto> => {
      const res = await fetch('/api/maps/config', { credentials: 'include' });
      if (!res.ok) throw new Error(`maps config: HTTP ${res.status}`);
      return (await res.json()) as MapsConfigDto;
    },
    staleTime: Infinity,
  });
}

export function mapAuthOptions(cfg: MapsConfigDto): atlas.AuthenticationOptions | null {
  if (cfg.mode === 'anonymous' && cfg.clientId) {
    return {
      authType: 'anonymous' as atlas.AuthenticationType,
      clientId: cfg.clientId,
      getToken: (resolve, reject) => {
        fetch('/api/maps/token', { credentials: 'include' })
          .then((r) => {
            if (!r.ok) throw new Error(`maps token: HTTP ${r.status}`);
            return r.text();
          })
          .then(resolve)
          .catch(reject);
      },
    };
  }
  if (cfg.mode === 'subscriptionKey' && cfg.subscriptionKey) {
    return {
      authType: 'subscriptionKey' as atlas.AuthenticationType,
      subscriptionKey: cfg.subscriptionKey,
    };
  }
  return null;
}

/** Basemap style matching the active portal theme (dark themes get the dark grayscale). */
export function useMapStyle(): string {
  const theme = useThemeStore((s) => s.theme);
  const mode = themes.find((t) => t.id === theme)?.mode ?? 'light';
  const dark =
    mode === 'dark' ||
    (mode === 'auto' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
  return dark ? 'grayscale_dark' : 'road';
}
